import type { ComponentInterface, EventEmitter } from '@stencil/core';
import { Component, Host, h, Prop, State, Watch, Event } from '@stencil/core';
import { slotAssignedElements } from "../../utils/dom";

interface LineStyle {
  width?: string;
  transform?: string;
}

@Component({
  tag: 'jackery-tabs',
  styleUrl: 'jackery-tabs.css',
  shadow: true,
})

export class JackeryTabs implements ComponentInterface {
  private slotRef?: any;
  private navRef?: any;

  /**
   * 当前激活的 tab，对应到 jackery-tab-content 中的 name
   */
  @Prop({mutable: true}) activekey: string = '0';
  @Watch('activekey')
  onIsKeyChange(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      this.handleChange(newValue);

      this.tabChange.emit({
        index: this.tabPos[this.activekey]?.index || 0,
        label: this.tabPos[this.activekey]?.label || ""
      });
    }
  }

  /**
   * 是否吸顶	
   */
  @Prop() sticky: boolean = false;

  /**
   * 深色模式	
   */
   @Prop() dark: boolean = false;

   /**
    * tabs切换标签显示区域宽度
    */
   @Prop() navWidth: string = 'auto';
 
   /**
    * 是否隐藏Tabs底边线	
    */
   @Prop() hideline: boolean = false;
 
   /**
    * 是否允许滑动切换
    * MB端：手指滑动标签内容区域左右切换
    */
   @Prop() allowslide: boolean = false;
 
   /**
    * Tabs均匀分布 
    * space-around
    */
   @Prop() tabaround: boolean = false;

  /**
   * Tab样式类型
   */
  @Prop() type?: 'line' | 'fill' = 'line';

  /**
   * Tab对齐方式
   */
  @Prop() navalign?: 'center' | 'left' | 'right' = 'center';

  /**
   * Tab item 背景颜色	
   * type 属性为 fill 时候生效
   */
  @Prop() tabbg: string = '#f5f5f5';

  /**
   * Tab item 选中状态背景颜色	
   * type 属性为 fill 时候生效
   */
  @Prop() activebg: string = '#FD5000';

  /**
   * Tab 位置
   */
  @Prop() tabpos?: 'top' | 'left' | 'right' | 'bottom' = 'top';

  /**
   * 容器最大高度	
   * tabpos 属性为 left | right 时候生效
   */
   @Prop() maxheight: string = '300px';

  /**
   * 吸顶时与顶部的距离offsettop(单位: vw)
   * sticky = true 时生效
   */
  @Prop() areatop: string = '0vw';

  /**
   * 下划线宽度	
   */
   @Prop() linewidth: string = '40px';

  /**
   * Tab Nav 高度	
   */
  @Prop() navheight: string = '48px';

  /**
   * Tab Nav 背景颜色	
   */
  @Prop() navbackground: string = 'transparent';

  /**
   * Tab Nav 字体大小	
   */
  @Prop() navsize: string = '18px';

  /**
   * Tab Nav 之间间隙
   * 在 tabaround 属性为false时候生效
   */
  @Prop() navgap: string = '40px';

  /**
   * 选中状态颜色
   */
  @Prop() activecolor: string = '#FD5000';

  /**
   * Tab Nav 常态字体颜色
   */
  @Prop() navcolor: string = '#333333';

  /**
   * Tab调用
   */
  @Event({ eventName: 'jkyTabChange' }) tabChange!:EventEmitter;

  @State() init: boolean = false;
  @State() tabNavs: Array<any> = [];
  @State() leftIndex: number = 0;
  @State() tabPos: any = {};
  @State() startX: number = 0;
  @State() startY: number = 0;
  @State() endX: number = 0;
  @State() endY: number = 0;
  @State() tabLineStyle: LineStyle = {};
  @State() actionsShow: boolean = false;

  private handleChange = (activekey) => {
    this.activekey = activekey ? activekey : '0'
    let active = this.tabPos[activekey];
    const assignedNodes = this.slotRef?.assignedElements({flatten:true});
    const elements = slotAssignedElements(assignedNodes);
    if (!active) {
      active = this.tabPos[this.activekey];
    }

    elements.forEach((item: any) => {
      if (item.name === this.activekey) {
        if(!item.disabled) {
          item.active = true
        }
      } else {
        item.active = false;
      }
    });

    this.navRef.parentNode.scrollLeft =
      active?.left +
      active?.width / 2 -
      this.navRef.parentNode.offsetWidth / 2;

    const pre = this.navRef.querySelector(`jackery-tab-nav[active]`);
    if (pre) {
      pre.active = false
    }
    const cur = this.navRef.querySelector(`jackery-tab-nav[name='${activekey}']`);
    if (cur) {
      cur.active = true
    }

    this.initTabLine();
  }

  private handleClick = (e: any, item: any) => {
    if (item.active || item.disabled) return;
    if(this.activekey === item.name) return
    console.log("handleClick----", e, item, this.activekey, item.name)
    this.activekey = item.name

    this.handleChange(item.name);
  }

  private handleWheel = (e: WheelEvent) => {
    if(this.tabpos === "top" || this.tabpos === "bottom") {
      e.preventDefault();
    }
    this.navRef.parentNode.scrollLeft += e.deltaY;
  }

  private handleTouchStart = (e: any) => {
    this.startX = e.changedTouches[0].clientX;
    this.startY = e.changedTouches[0].clientY;
    this.endX = 0;
    this.endY = 0;
  }

  private handleTouchMove = (e: any) => {
    this.endX = e.changedTouches[0].clientX;
    this.endY = e.changedTouches[0].clientY;
  }

  private handleTouchEnd = () => {
    const angle = this.angle(
      { X: this.startX, Y: this.startY },
      { X: this.endX, Y: this.endY }
    );
    if (this.endX === undefined || this.endY === undefined) {
      return;
    }
    // 点击事件
    if (this.endX === 0) {
      return;
    }
    if (Math.abs(angle) > 30) {
      return;
    }
    if (this.endX > this.startX) { // 右滑
      this.prevSlider();
    } else { // 左滑
      this.nextSlider();
    }
  }

  private prevSlider = () => {
    const active = this.tabPos[this.activekey];
    if (!active) return;
    const keys = Object.keys(this.tabPos);
    if (active.index <= 0 && keys.length > 0) {
      this.activekey = keys[keys.length - 1];
      return;
    }
    const preIndex = active.index - 1;
    let activeKey;
    let prevItem;
    keys.forEach((key) => {
      const item = this.tabPos[key];
      if (item.index === preIndex) {
        activeKey = key;
        prevItem = item;
      }
    });
    if (prevItem && prevItem.disabled) {
      this.activekey = activeKey;
      this.prevSlider();
      return;
    }

    if (!activeKey) {
      return;
    }
    this.activekey = activeKey;
    // this.emitChange(this.tabPos[this.activekey]);
  }

  private nextSlider = () => {
    const active = this.tabPos[this.activekey];
    if (!active) {
      return;
    }
    const keys = Object.keys(this.tabPos);
    if (active.index >= keys.length - 1) {
      this.activekey = keys[0];
      return;
    }
    const nextIndex = active.index + 1;
    let activeKey;
    let nextItem;
    keys.forEach((key) => {
      const item = this.tabPos[key];
      if (item.index === nextIndex) {
        activeKey = key;
        nextItem = item;
      }
    });
    if (nextItem && nextItem.disabled) {
      this.activekey = activeKey;
      this.nextSlider();
      return;
    }
    if (!activeKey) {
      return;
    }
    this.activekey = activeKey;
  }

  private angle = (start: any, end: any) => {
    const X = end.X - start.X;
    const Y = end.Y - start.Y;
    return (360 * Math.atan(Y / X)) / (2 * Math.PI);
  }

  private renderTabNav = () => {
    return (
      <div class="jackery-tab-nav-con" part="nav-con" onWheel={(e) => this.handleWheel(e)}>
        <div
          class={`jackery-tab-nav jackery-tab-nav__flex`}
          part="tab-navs"
          ref={(navRef) => (this.navRef = navRef)}
        >
          {this.tabNavs.map((item) => (
            <jackery-tab-nav
              part="tab-nav"
              active={item.name === this.activekey}
              disabled={item.disabled}
              dark={item.dark}
              name={item.name}
              type={this.type}
              tabbg={this.tabbg}
              activebg={this.activebg}
              onClick={(e: any) => this.handleClick(e, item)}
            >
              {item.label}
            </jackery-tab-nav>
          ))}
          <i 
            class="jackery-tab-line" 
            part="tab line"
            style={{
              ...this.tabLineStyle
            }}
          ></i>
        </div>
      </div>
    )
  }

  private initTabNavs = () => {
    const assignedNodes = this.slotRef?.assignedElements({flatten:true});
    const elements = slotAssignedElements(assignedNodes);
    elements.forEach((item: any, index: number) => {
      if (!item.name) {
        item.name = String(index);
      }
      this.tabNavs.push({
        disabled: !!item.disabled,
        name: item.name,
        label: item.label,
        dark: this.dark
      });
    });
    setTimeout(() => { this.init = true; }, 20);
  };

  private initTabContent = () => {
    console.log("initTabContent===", this.activekey, this.tabPos)
    let active = this.tabPos[this.activekey];
    const assignedNodes = this.slotRef?.assignedElements({flatten:true});
    const elements = slotAssignedElements(assignedNodes);
    if(!active) {
      this.activekey = elements[0].name;
      active = this.tabPos[this.activekey];
    }

    elements.forEach((item: any) => {
      if (item.name === this.activekey) {
        if(!item.disabled) {
          item.active = true
        }
      } else {
        item.active = false
      }
    });
    this.leftIndex = active?.index;
    this.tabLineStyle = this.getLineStyle(active?.width, active?.left, active?.height, active?.top);
  }

  private initTabLine = () => {
    const items = this.navRef?.querySelectorAll("jackery-tab-nav");
    if(items && items.length) {
      Array.from(items).forEach((item: any, index) => {
        this.tabPos[item.name] = {
          index: index,
          width: item.offsetWidth,
          height: item.offsetHeight,
          left: item.offsetLeft,
          top: item.offsetTop,
          label: item.textContent,
          disabled: item.disabled,
        };
      });

      setTimeout(() => { this.initTabContent(); }, 20);
    } else {
      setTimeout(() => { this.initTabLine() }, 100);
    }
  }

  private getLineStyle = (labelWidth: number, labelOffset: number, labelHeight: number, labelTop: number) => {
    let lineWidth: string | number = this.linewidth;
    let lineHeight: string | number = this.navheight;
    if (lineWidth.includes("px")) {
      lineWidth.replace("px", "");
    }
    if (lineHeight.includes("px")) {
      lineHeight.replace("px", "");
    }
    lineWidth = parseInt(lineWidth, 10) || labelWidth;
    lineHeight = parseInt(lineHeight, 10) || labelHeight;
    let tranX = labelOffset + (labelWidth - lineWidth) * 0.5;
    let tranY = labelTop + (labelHeight - lineHeight) * 0.5;
    if(!labelWidth) { // FIX: 初始化DOM未完全渲染计算
      tranX = 0
      tranY = 0
    }

    if(this.tabpos === "left" || this.tabpos === "right") {
      return {
        height: `${lineHeight}px`,
        transform: `translateY(${tranY}px)`
      }
    } else {
      return {
        width: `${lineWidth}px`,
        transform: `translateX(${tranX}px)`
      }
    }
  }

  private handleSlotChange = () => {}

  componentDidRender() {
    if(this.init) return
    this.initTabNavs();
    setTimeout(() => {this.initTabLine();}, 0);

    setTimeout(() => {
      console.log("this.navRef", this.navRef.offsetWidth, this.navRef.parentNode.offsetWidth)
      if(this.navRef.parentNode.offsetWidth < this.navRef.offsetWidth) {
        this.actionsShow = true
        console.log("this.actionsShow", this.actionsShow)
      }
    }, 200);
  }


  render() {
    const style = {
      transform: `translateX(${-this.leftIndex * 100}%)`,
    };

    return (
      <Host
        class={{
          "hideline": this.hideline,
          "tabaround": this.tabaround,
          "sticky": this.sticky,
          [`position--${this.tabpos}`]: true,
          [`type--${this.type}`]: true,
          [`align--${this.navalign}`]: true,
        }}

        style={{
          "--nav-height": this.navheight,
          "--nav-background": this.navbackground,
          "--nav-size": this.navsize,
          "--nav-active": this.activecolor,
          "--nav-navgap": this.navgap,
          "--nav-color": this.navcolor,
          "--maxheight": this.maxheight,
          "--activebg": this.activebg,
          "--tabbg": this.tabbg,
          "--areatop": this.areatop,
        }}
      >
        <div class="jackery-tabs">
          <div 
            class="nav-container"
            style={
              {
                width: `${this.navWidth}`,
              }
            }
          >
          {this.actionsShow &&
            <div class="jackery-tabs-nav-operations jackery-tabs-nav-operations--prev" onClick={() => this.prevSlider()}>
                <svg class="icon" viewBox="0 0 1024 1024">
                    <path d="M311.210667 140.458667a42.666667 42.666667 0 0 1 56.32-3.498667l4.010666 3.584 341.333334 342.186667a42.666667 42.666667 0 0 1 3.498666 56.32l-3.584 4.010666-341.333333 340.48a42.666667 42.666667 0 0 1-63.829333-56.405333l3.584-4.010667 311.082666-310.357333-311.168-311.978667a42.666667 42.666667 0 0 1-3.498666-56.32l3.584-4.010666z" p-id="3630"></path>
                </svg>
            </div>
          }

            {this.renderTabNav()}
            
          {this.actionsShow &&
            <div class="jackery-tabs-nav-operations" onClick={() => this.nextSlider()}>
                <svg class="icon" viewBox="0 0 1024 1024">
                    <path d="M311.210667 140.458667a42.666667 42.666667 0 0 1 56.32-3.498667l4.010666 3.584 341.333334 342.186667a42.666667 42.666667 0 0 1 3.498666 56.32l-3.584 4.010666-341.333333 340.48a42.666667 42.666667 0 0 1-63.829333-56.405333l3.584-4.010667 311.082666-310.357333-311.168-311.978667a42.666667 42.666667 0 0 1-3.498666-56.32l3.584-4.010666z" p-id="3630"></path>
                </svg>
            </div>
          }
            
          </div>

          <div
            class="jackery-tab-content"
            part="content"
            onTouchStart={(e) => this.allowslide && this.handleTouchStart(e)}
            onTouchMove={(e) => this.allowslide && this.handleTouchMove(e)}
            onTouchEnd={() => this.allowslide && this.handleTouchEnd()}
          >
            <div class="jackery-tab-content-wrap" part="content-wrap" style={style}>
              <slot ref={(slotRef) => (this.slotRef = slotRef)} onSlotchange={this.handleSlotChange}></slot>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
